import React from "react";
import "./LandingPage.css";
import { useState } from "react";

const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://balkan-dev-champs-email-func-dfg8dpfwh6d7h3ak.westeurope-01.azurewebsites.net";

function LandingPage() {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [hasError, setHasError] = useState(false);
    
    const handleContactSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        setIsSubmitting(true);

        const formData = new FormData(e.target);
        const payload = {
            email: formData.get("email"),
            project: formData.get("project") || null,
            stack: formData.get("stack") || null,
            budget: formData.get("budget") || null,
        };

        try {
            const res = await fetch(`${API_BASE_URL}/api/ContactForm`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!res.ok) throw new Error("Request failed");

            setIsSuccess(true);
            setHasError(false);
            formData.reset();
        } catch (err) {
            console.error(err);
            setHasError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="landing-root">
            
            <main className="landing-main">
                <div className="lp-page">
                    {/* NAVBAR */}
                    <header className="lp-hero">
                        <nav className="lp-nav">
                            <div className="lp-logo">
                                BalkanDevChamps<span>.</span>
                            </div>
                            <ul className="lp-nav-links">
                                <li>
                                    <a href="#how-it-works">How it works</a>
                                </li>
                                <li>
                                    <a href="#why-trust-us">Why both sides trust us</a>
                                </li>
                                <li>
                                    <a href="#who-we-serve">Who we serve</a>
                                </li>
                                <li>
                                    <a href="#contact">Contact</a>
                                </li>
                            </ul>
                            <a href="#contact" className="lp-nav-cta">
                                Book intro call
                            </a>
                        </nav>

                        {/* HERO */}
                        <div className="lp-hero-content">
                            <div className="lp-hero-text">
                                <h1>
                                    Plug into vetted Balkan dev teams
                                    <br />
                                    without agency bloat or guesswork.
                                </h1>
                                <p>
                                    We match <strong>US &amp; EU SaaS / fintech / scale-ups</strong> with{" "}
                                    <strong>pre-vetted dev shops in Bulgaria &amp; Eastern Europe</strong>.
                                    You only pay when you sign a deal. No retainers, no fluff.
                                </p>
                                <div className="lp-hero-actions">
                                    <a href="#contact" className="lp-btn lp-btn-primary">
                                        Get 3 dev teams shortlist
                                    </a>
                                    <a href="#how-it-works" className="lp-btn lp-btn-secondary">
                                        See how it works
                                    </a>
                                </div>
                                <div className="lp-hero-badges">
                                    <span>✓ Commission-based only</span>
                                    <span>✓ Vetted partner companies</span>
                                    <span>✓ Clear SLAs &amp; expectations</span>
                                </div>
                            </div>

                            <div className="lp-hero-card">
                                <h2>Quick snapshot</h2>
                                <ul>
                                    <li>
                                        <strong>Ideal for:</strong> SaaS, fintech, product teams
                                    </li>
                                    <li>
                                        <strong>Regions:</strong> Bulgaria, Romania, Balkans
                                    </li>
                                    <li>
                                        <strong>Models:</strong> Dedicated teams / staff aug
                                    </li>
                                    <li>
                                        <strong>Our fee:</strong> % of deal paid by dev shop
                                    </li>
                                </ul>
                                <p className="lp-hero-card-note">
                                    You get a curated shortlist. They get qualified deals. We get paid only when
                                    there’s a match.
                                </p>
                            </div>
                        </div>
                    </header>

                    {/* HOW IT WORKS */}
                    <section id="how-it-works" className="lp-section">
                        <div className="lp-section-inner">
                            <h2>How it works</h2>
                            <p className="lp-section-subtitle">
                                A simple 3-step process designed to protect <strong>both</strong> the buyer and
                                the dev shop.
                            </p>
                            <div className="lp-steps">
                                <div className="lp-step">
                                    <div className="lp-step-number">1</div>
                                    <h3>We clarify what you need</h3>
                                    <p>
                                        Short call or form: tech stack, team size, budget range, time zones, start
                                        date, and must-haves.
                                    </p>
                                </div>
                                <div className="lp-step">
                                    <div className="lp-step-number">2</div>
                                    <h3>We match you with vetted partners</h3>
                                    <p>
                                        We shortlist 2–5 dev companies from Bulgaria/region that match your tech,
                                        culture, and budget.
                                    </p>
                                </div>
                                <div className="lp-step">
                                    <div className="lp-step-number">3</div>
                                    <h3>Warm intros &amp; deal support</h3>
                                    <p>
                                        We facilitate intros, help you compare offers, and support until a deal is
                                        signed. Our fee is paid by the dev company.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* WHY BOTH SIDES TRUST US */}
                    <section id="why-trust-us" className="lp-section lp-section-alt">
                        <div className="lp-section-inner">
                            <h2>Why both sides trust us</h2>
                            <p className="lp-section-subtitle">
                                We’re not a body-shop or random broker. We protect both the product team and the
                                dev shop, so everyone stays in the deal long-term.
                            </p>

                            <div className="lp-two-columns lp-card-columns">
                                {/* LEFT – dev shops */}
                                <div id="for-devshops" className="lp-trust-card">
                                    <h3>For dev shops in Bulgaria &amp; Eastern Europe</h3>
                                    <ul className="lp-list">
                                        <li>
                                            We pre-qualify buyers so you don’t waste time on dead leads and
                                            pointless RFPs.
                                        </li>
                                        <li>
                                            Clear expectations on budget, tech stack and engagement model before
                                            intro calls.
                                        </li>
                                        <li>
                                            No retainers – we get paid only when you sign a client, from your
                                            success fee.
                                        </li>
                                        <li>
                                            We help you position your strengths: processes, delivery and
                                            communication, not just hourly rate.
                                        </li>
                                        <li>
                                            Long-term partner: we’d rather send you 3 right clients than 30 random
                                            ones.
                                        </li>
                                    </ul>
                                </div>

                                {/* RIGHT – US/EU buyers */}
                                <div id="for-buyers" className="lp-trust-card">
                                    <h3>For US &amp; EU product &amp; engineering leaders</h3>
                                    <ul className="lp-list">
                                        <li>
                                            Only vetted engineering partners with real references and delivery
                                            track record.
                                        </li>
                                        <li>
                                            Transparent pricing, team structure and SLAs – no surprise mark-ups.
                                        </li>
                                        <li>
                                            You speak with senior decision-makers, not random sales reps.
                                        </li>
                                        <li>
                                            Focus on culture fit, communication and time-zone overlap, not just
                                            “cheap devs”.
                                        </li>
                                        <li>
                                            We stay in the loop after the intro to help if anything goes off-track.
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* WHO WE SERVE */}
                    <section id="who-we-serve" className="lp-section">
                        <div className="lp-section-inner">
                            <h2>Who we’re a great fit for</h2>
                            <div className="lp-grid-3">
                                <div className="lp-card">
                                    <h3>SaaS &amp; product teams</h3>
                                    <p>
                                        You’ve found PMF or are close, and you need reliable developers to ship
                                        features, not babysit freelancers.
                                    </p>
                                </div>
                                <div className="lp-card">
                                    <h3>Fintech &amp; regulated</h3>
                                    <p>
                                        You care about compliance, uptime, and security. We match you with teams
                                        used to stricter standards.
                                    </p>
                                </div>
                                <div className="lp-card">
                                    <h3>Growing dev agencies</h3>
                                    <p>
                                        Balkan dev shops that want stable foreign clients and are ready to invest
                                        in long-term partnerships.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* LEAN MODEL / METRICS */}
                    <section className="lp-section lp-section-alt">
                        <div className="lp-section-inner lp-two-columns">
                            <div>
                                <h2>Lean, bootstrap-friendly model</h2>
                                <p className="lp-section-subtitle">
                                    You already have an IT job. This model lets you grow slowly and sanely without
                                    burning savings.
                                </p>
                                <ul className="lp-list">
                                    <li>Start with 1–3 vetted dev companies you trust.</li>
                                    <li>Set a simple, transparent commission % per deal.</li>
                                    <li>Do focused outreach instead of spammy mass prospecting.</li>
                                    <li>Turn every “no” into intel for better matches.</li>
                                </ul>
                            </div>

                            <div className="lp-metrics">
                                <div className="lp-metric">
                                    <span className="lp-metric-number">3–6</span>
                                    <span className="lp-metric-label">months to first stable client</span>
                                </div>
                                <div className="lp-metric">
                                    <span className="lp-metric-number">1–3</span>
                                    <span className="lp-metric-label">dev shops to start with</span>
                                </div>
                                <div className="lp-metric">
                                    <span className="lp-metric-number">0</span>
                                    <span className="lp-metric-label">retainers / hidden fees</span>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* FAQ */}
                    <section className="lp-section">
                        <div className="lp-section-inner">
                            <h2>FAQ</h2>
                            <div className="lp-faq">
                                <div className="lp-faq-item">
                                    <h3>Who pays your commission?</h3>
                                    <p>
                                        The dev company. We agree a % of the deal value with them in advance.
                                        Buyers don’t pay anything to be matched.
                                    </p>
                                </div>
                                <div className="lp-faq-item">
                                    <h3>What about time zones &amp; political risk?</h3>
                                    <p>
                                        We address this upfront: we set expectations on overlap hours,
                                        communication habits, and contingency plans. We only introduce companies
                                        we believe can deliver reliably.
                                    </p>
                                </div>
                                <div className="lp-faq-item">
                                    <h3>Do you work exclusively with one dev company?</h3>
                                    <p>
                                        No. We prefer to have a small pool of trusted partners so we can match
                                        based on tech stack, culture, and budget instead of pushing one option to
                                        everyone.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* CONTACT / CTA */}
                    <section id="contact" className="lp-section lp-section-alt">
                        <div className="lp-section-inner lp-contact">
                            <div>
                                <h2>Want a curated shortlist of dev partners?</h2>
                                <p className="lp-section-subtitle">
                                    Tell us what you’re building, your budget range, and timing. We’ll get back
                                    with a call link and initial questions.
                                </p>
                                <ul className="lp-list">
                                    <li>No spam. No random agencies.</li>
                                    <li>Just a focused conversation about your needs.</li>
                                    <li>If we can’t help, we’ll say so openly.</li>
                                </ul>
                            </div>
                            <div className="lp-contact-box">
                                {!isSuccess && (
                                <form
                                    className="lp-form"
                                    onSubmit={handleContactSubmit}
                                >
                                    <label>
                                        Work email
                                        <input
                                            type="email"
                                            name="email"
                                            placeholder="you@company.com"
                                            required
                                        />
                                    </label>
                                    <label>
                                        What are you building?
                                        <textarea
                                            name="project"
                                            placeholder="SaaS / fintech / internal tools... briefly explain what you need help with."
                                            rows={3}
                                            required
                                        />
                                    </label>
                                    <label>
                                        Tech stack / roles needed
                                        <input
                                            type="text"
                                            name="stack"
                                            placeholder="e.g. React, Node, .NET, QA, DevOps..."
                                        />
                                    </label>
                                    <label>
                                        Budget range (monthly)
                                        <input
                                            type="text"
                                            name="budget"
                                            placeholder="e.g. €8k–€25k / month"
                                        />
                                    </label>
                                    <button
                                        type="submit"
                                        className="lp-btn lp-btn-primary lp-btn-full"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <span className="lp-loader" />
                                        ) : (
                                            "Request intro call"
                                        )}
                                    </button>
                                    
                                </form> )}

                                {isSuccess && (
                                    <div className="lp-confirmation">
                                        <h3>Thanks — request received 🎉</h3>
                                        <p>
                                            We’ll review your request and email you a short intro call link.
                                        </p>
                                        <p className="lp-confirmation-note">
                                            If it’s not a fit, we’ll tell you openly.
                                        </p>
                                        <button
                                            className="lp-btn lp-btn-secondary lp-btn-full"
                                            onClick={() => {
                                                setIsSuccess(false);
                                                setHasError(false);
                                            }}
                                        >
                                            Submit another request
                                        </button>
                                    </div>
                                )}
                                <p className="lp-contact-note">
                                    Prefer email? Reach out at{" "}
                                    <a href="mailto:georgi@balkandevchamps.com">georgi@balkandevchamps.com</a>
                                </p>
                                {hasError && !isSuccess && (
                                    <p className="lp-form-message lp-form-error">
                                        ❌ Something went wrong. Please try again or email us directly.
                                    </p>
                                )}
                            </div>
                        </div>
                    </section>
                    

                    {/* FOOTER */}
                    <footer className="lp-footer">
                        <div className="lp-footer-inner">
                            <span>© {new Date().getFullYear()} BalkanDevChamps. All rights reserved.</span>
                            <span className="lp-footer-links">
                                <a href="#how-it-works">Process</a>
                                <a href="#who-we-serve">Who we serve</a>
                                <a href="#contact">Contact</a>
                            </span>
                        </div>
                    </footer>
                </div>
            </main>
        </div>
    );
}

export default LandingPage;
